<template>
  <div ref="chartContainer" class="hex-heatmap flex justify-center" style="width: 100%; height: 100%; position: relative;">
    <svg ref="heatmapSvg"/>
    <!-- Tooltip element -->
    <div
      ref="tooltip"
      style="position: absolute; pointer-events: none; background: rgba(0,0,0,0.85); color: #fff; padding: 8px 12px; border-radius: 6px; font-size: 12px; display: none; z-index: 10;">
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import { hexbin as d3Hexbin } from 'd3-hexbin'
import { isDemoMode, spoofText } from '@/utils/demoSpoof'

export default {
  name: 'HexagonalHeatmap',
  props: {
    chartData: {
      type: Array,
      required: true
    },
    chartConfig: {
      type: Object,
      default: () => ({
        margin: { top: 40, right: 40, bottom: 80, left: 80 }, // Doubled the spacing
        aspectRatio: 'wide', // 'square', 'wide', or 'tall'
        xLabel: 'Coding Intensity',
        yLabel: 'Recapture Performance',
        heatLabel: 'Avg HCC Score V28',
        maxHexRadius: 30
      })
    }
  },
  data() {
    return {
      svg: null,
      width: 800,
      height: 600,
      hexRadius: 20,
      gridDimensions: { rows: 0, cols: 0 },
      colorScale: null,
      hexbin: null
    }
  },
  computed: {
    // Reactive Demo Mode state: an already-rendered map repaints its provider
    // labels when masking starts or stops, even if its data does not change.
    demoMasking() {
      return isDemoMode()
    },
    sorted2DData() {
      // Create a copy of the data with indices
      const dataWithIndices = this.chartData.map((d, i) => ({ ...d, originalIndex: i }))

      // Always sort ascending for both axes
      const ySorted = [...dataWithIndices].sort((a, b) => {
        const aVal = a.y || 0
        const bVal = b.y || 0
        return aVal - bVal // Always ascending
      })

      // Divide into rows
      const { rows, cols } = this.gridDimensions
      const itemsPerRow = Math.ceil(ySorted.length / rows)

      // Create 2D array structure
      const sorted2D = []
      for (let row = 0; row < rows; row++) {
        const rowStart = row * itemsPerRow
        const rowEnd = Math.min(rowStart + itemsPerRow, ySorted.length)
        const rowData = ySorted.slice(rowStart, rowEnd)

        // Sort this row by X (always ascending)
        rowData.sort((a, b) => {
          const aVal = a.x || 0
          const bVal = b.x || 0
          return aVal - bVal // Always ascending
        })

        sorted2D.push(rowData)
      }

      return sorted2D
    }
  },
  methods: {
    updateDimensions() {
      const container = this.$refs.chartContainer
      if (container) {
        const { width, height } = container.getBoundingClientRect()
        this.width = width
        this.height = height
      }
    },

    calculateGridDimensions(n) {
      const { aspectRatio } = this.chartConfig
      let cols, rows

      switch(aspectRatio) {
        case 'wide':
          rows = Math.ceil(Math.sqrt(n / 1.5))
          cols = Math.ceil(n / rows)
          break
        case 'tall':
          cols = Math.ceil(Math.sqrt(n / 1.5))
          rows = Math.ceil(n / cols)
          break
        case 'square':
        default:
          cols = Math.ceil(Math.sqrt(n))
          rows = Math.ceil(n / cols)
          break
      }

      return { rows, cols }
    },

    calculateHexRadius() {
      const { margin, maxHexRadius } = this.chartConfig
      const innerWidth = this.width - margin.left - margin.right
      const innerHeight = this.height - margin.top - margin.bottom

      const radiusFromWidth = innerWidth / ((this.gridDimensions.cols + 0.5) * Math.sqrt(3))
      const radiusFromHeight = innerHeight / ((this.gridDimensions.rows + 1/3) * 1.5)

      return Math.min(radiusFromWidth, radiusFromHeight, maxHexRadius)
    },

    calculate2DHexCenters() {
      const centers = []
      const sorted2D = this.sorted2DData

      // Iterate through the 2D sorted structure
      sorted2D.forEach((rowData, row) => {
        rowData.forEach((dataPoint, col) => {
          let x = this.hexRadius * col * Math.sqrt(3)
          // Offset odd rows
          if (row % 2 === 1) {
            x += (this.hexRadius * Math.sqrt(3)) / 2
          }
          const y = this.hexRadius * row * 1.5

          centers.push({
            x,
            y,
            data: dataPoint,
            row: row,
            col: col
          })
        })
      })

      return centers
    },

    getColorScale() {
      const heatExtent = d3.extent(this.chartData, d => d.heat)

      // Fixed green to red color scale
      return d3.scaleSequential(d3.interpolateRdYlGn)
        .domain([heatExtent[1], heatExtent[0]]) // Reversed so high values are red
    },

    renderHeatmap() {
      this.updateDimensions()

      const { margin, xLabel, yLabel, heatLabel } = this.chartConfig

      // Calculate grid dimensions
      this.gridDimensions = this.calculateGridDimensions(this.chartData.length)

      // Calculate hex radius
      this.hexRadius = this.calculateHexRadius()

      // Clear previous content
      d3.select(this.$refs.heatmapSvg).selectAll('*').remove()

      // Configure svg
      this.svg = d3.select(this.$refs.heatmapSvg)
        .attr('width', this.width)
        .attr('height', this.height)

      // Create main group
      const g = this.svg.append('g')
        .attr('transform', `translate(${margin.left + this.hexRadius},${margin.top + this.hexRadius})`)

      // Initialize hexbin
      this.hexbin = d3Hexbin().radius(this.hexRadius)

      // Get color scale
      this.colorScale = this.getColorScale()

      // Calculate 2D sorted centers
      const centers = this.calculate2DHexCenters()

      // Provider label + NPI are painted into the D3 hover tooltip (innerHTML in a
      // detached node the DOM spoof engine never sees). Spoof them onto a clone of
      // the data point so we don't mutate the source prop.
      if (isDemoMode()) {
        centers.forEach((c) => {
          if (!c.data) return
          c.data = {
            ...c.data,
            label: c.data.label ? spoofText(c.data.label) : c.data.label,
            npi: c.data.npi ? spoofText(String(c.data.npi)) : c.data.npi,
          }
        })
      }

      // Tooltip
      const tooltip = d3.select(this.$refs.tooltip)
      const config = this.chartConfig
      const hexRadius = this.hexRadius
      const colorScale = this.colorScale
      const hexbin = this.hexbin

      // Draw hexagons
      g.selectAll('.hexagon')
        .data(centers)
        .enter()
        .append('path')
        .attr('class', 'hexagon')
        .attr('d', d => `M${d.x},${d.y}${hexbin.hexagon()}`)
        .style('fill', d => {
          if (d.data) {
            return colorScale(d.data.heat)
          }
          return '#f7fafc'
        })
        .style('stroke', '#fff')
        .style('stroke-width', 1.5)
        .style('cursor', d => d.data ? 'pointer' : 'default')
        .style('transition', 'fill 0.3s ease')
        .on('mouseover', function(e, d) {
          if (!d.data) return

          const dataPoint = d.data

          // Enhance hexagon
          d3.select(this)
            .style('stroke', '#2d3748')
            .style('stroke-width', 2.5)
            .style('filter', 'brightness(1.1)')

          // Build tooltip content with position info
          let tooltipContent = `<strong>${dataPoint.label || `Provider ${dataPoint.npi || dataPoint.originalIndex + 1}`}</strong><br/>`
          tooltipContent += `<span style="color: #94a3b8; font-size: 10px;">Row ${d.row + 1}, Col ${d.col + 1}</span><br/>`

          // Add x value (coding intensity)
          if (dataPoint.x !== undefined) {
            tooltipContent += `${config.xLabel}: ${dataPoint.x.toFixed(3)}<br/>`
          }

          // Add y value (recapture rate)
          if (dataPoint.y !== undefined) {
            const percentage = (dataPoint.y * 100).toFixed(1)
            tooltipContent += `${config.yLabel}: ${percentage}%<br/>`
          }

          // Add heat value (Avg HCC Score V28)
          if (dataPoint.heat !== undefined) {
            tooltipContent += `${config.heatLabel}: ${dataPoint.heat.toFixed(3)}`
          }

          tooltip
            .style('display', 'block')
            .html(tooltipContent)
        })
        .on('mousemove', function(e) {
          tooltip
            .style('left', (e.offsetX + 10) + 'px')
            .style('top', (e.offsetY - 10) + 'px')
        })
        .on('mouseout', function(e, d) {
          // Reset hexagon
          d3.select(this)
            .style('stroke', '#fff')
            .style('stroke-width', 1.5)
            .style('filter', 'none')

          tooltip.style('display', 'none')
        })

      // Add axis indicators
      this.addAxisIndicators(g)

      // Add grid info text
      g.append('text')
        .attr('class', 'grid-info')
        .attr('x', 0)
        .attr('y', -this.hexRadius/2)
        .attr('font-size', '11px')
        .attr('fill', '#718096')
        .text(`Grid: ${this.gridDimensions.cols}×${this.gridDimensions.rows} | Points: ${this.chartData.length}`)
    },

    addAxisIndicators(g) {
      const { xLabel, yLabel } = this.chartConfig
      const { rows, cols } = this.gridDimensions

      // Calculate positions for axis labels
      const gridWidth = (cols - 0.5) * this.hexRadius * Math.sqrt(3)
      const gridHeight = (rows - 0.5) * this.hexRadius * 1.5

      // X-axis label (always low to high)
      g.append('text')
        .attr('class', 'x-axis-indicator')
        .attr('text-anchor', 'middle')
        .attr('x', gridWidth / 2)
        .attr('y', gridHeight + 50) // Increased spacing
        .attr('font-size', '12px')
        .attr('fill', '#4a5568')
        .text(`→ ${xLabel} →`)

      // Y-axis label (always low to high)
      g.append('text')
        .attr('class', 'y-axis-indicator')
        .attr('text-anchor', 'middle')
        .attr('transform', `rotate(-90)`)
        .attr('x', -gridHeight / 2)
        .attr('y', -50) // Increased spacing
        .attr('font-size', '12px')
        .attr('fill', '#4a5568')
        .text(`↑ ${yLabel} ↑`)

      // Add subtle gradient indicators
      g.append('text')
        .attr('x', 0)
        .attr('y', gridHeight + 25) // Increased spacing
        .attr('font-size', '10px')
        .attr('fill', '#94a3b8')
        .attr('text-anchor', 'start')
        .text('Low')

      g.append('text')
        .attr('x', gridWidth)
        .attr('y', gridHeight + 25) // Increased spacing
        .attr('font-size', '10px')
        .attr('fill', '#94a3b8')
        .attr('text-anchor', 'end')
        .text('High')

      g.append('text')
        .attr('x', -25) // Increased spacing
        .attr('y', 0)
        .attr('font-size', '10px')
        .attr('fill', '#94a3b8')
        .attr('text-anchor', 'end')
        .text('High')

      g.append('text')
        .attr('x', -25) // Increased spacing
        .attr('y', gridHeight)
        .attr('font-size', '10px')
        .attr('fill', '#94a3b8')
        .attr('text-anchor', 'end')
        .text('Low')
    },

    onResize() {
      this.renderHeatmap()
    }
  },
  watch: {
    demoMasking() {
      d3.select(this.$refs.tooltip).style('display', 'none').html('')
      this.renderHeatmap()
    },
    chartData: {
      deep: true,
      handler() {
        this.renderHeatmap()
      }
    },
    chartConfig: {
      deep: true,
      handler() {
        this.renderHeatmap()
      }
    }
  },
  mounted() {
    this.renderHeatmap()
    window.addEventListener('resize', this.onResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize)
  }
}
</script>

<style scoped>
.hex-heatmap {
  display: block;
}

.hexagon {
  opacity: 0.95;
}

.hexagon:hover {
  opacity: 1;
}
</style>
